/**
 * ClubEventsPage - Mobile-First Club Events View
 *
 * Shows all events for a specific club with filtering and admin controls.
 * Uses EventCard components in mobile-first layout.
 * Supports both list and calendar views (#1059).
 *
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7D
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClub } from '../hooks/useClubs';
import { useClubEvents } from '../hooks/useEvents';
import { useRegisterFab } from '../contexts/PWAContext';
import { TopBar, EventCard, Button, EmptyState, Avatar, EventCardSkeleton, CalendarView, CalendarDayModal } from '../components';
import { Plus, Calendar, Users, List, CalendarDays } from 'lucide-react';

type FilterTab = 'upcoming' | 'all' | 'past';
type ViewMode = 'list' | 'calendar';

// Type for calendar event data
interface CalendarEventData {
  id: string;
  short_code: string;
  title: string;
  starts_at: number;
  location_name: string | null;
  hero_image_url: string | null;
  rsvp_count?: number;
}

export default function ClubEventsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterTab>('upcoming');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Calendar day modal state (#1059)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarEventData[]>([]);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);

  // Fetch club details
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');

  // Calculate current timestamp for filtering (updated every minute)
  const [currentTimestamp, setCurrentTimestamp] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimestamp(Math.floor(Date.now() / 1000));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Calendar mode shows all events; list mode respects filter
  const eventFilter = useMemo(() => {
    if (viewMode === 'calendar') {
      // For calendar view, fetch a wider range of events (past 3 months to future 1 year)
      const threeMonthsAgo = currentTimestamp - (90 * 24 * 60 * 60);
      const oneYearAhead = currentTimestamp + (365 * 24 * 60 * 60);
      return { from: threeMonthsAgo, to: oneYearAhead };
    }
    // List mode filtering
    if (activeFilter === 'upcoming') return { from: currentTimestamp };
    if (activeFilter === 'past') return { to: currentTimestamp };
    return undefined; // 'all' = no filter
  }, [viewMode, activeFilter, currentTimestamp]);

  // Fetch events
  const { data: events = [], isLoading: eventsLoading, error: eventsError } = useClubEvents(
    clubData?.club.id || '',
    eventFilter
  );

  // Handler for calendar day click (#1059)
  const handleDayClick = (date: Date, dayEvents: { id: string; title: string; starts_at: number }[]) => {
    // Map to full event data
    const fullDayEvents = dayEvents
      .map(de => events.find(e => e.id === de.id))
      .filter((e): e is NonNullable<typeof e> => e !== undefined);

    setSelectedDate(date);
    setSelectedDayEvents(fullDayEvents);
    setIsDayModalOpen(true);
  };

  const loading = clubLoading || eventsLoading;
  const error = clubError || eventsError;
  const club = clubData?.club;
  const membership = clubData?.membership;
  // #2176: include is_owner — a transferred-in owner has role 'owner' (not
  // 'leader'), so a role-only gate silently hides Create. Match ClubDetailPage.tsx.
  const isLeader = membership?.role === 'leader' || membership?.is_owner;
  // Suppress the PWA install prompt while the create-event FAB is on screen (#3096).
  // The FAB renders only once the leader-gated main content is shown, so mirror
  // that condition rather than registering during the loading/error skeleton
  // where no FAB exists (matches ClubEventsManagePage.tsx).
  useRegisterFab(!loading && !error && !!club && !!slug && !!isLeader);

  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Events" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <EventCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !club || !slug) {
    const errorMessage = error 
      ? 'Failed to load events'
      : !club 
      ? 'Club not found'
      : 'Invalid club';
      
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Events" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<Calendar className="w-12 h-12" />}
              title="Unable to load events"
              message={errorMessage}
              actionLabel="Back to Club"
              onAction={() => navigate(`/clubs/${slug}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Events"
        onBack={() => navigate(-1)}
      />
      
      <div className="flex-1 overflow-y-auto pt-14">
        <div className="px-4 py-6 space-y-6">
          {/* Club Identity Card */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
            <div className="flex items-center gap-3">
              <Avatar
                name={club.name}
                profile_image_url={club.hero_image_url}
                size="md"
                className="shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-text-primary truncate">
                  {club.name}
                </h2>
                <div className="flex items-center gap-1 text-text-muted text-sm">
                  <Users className="w-3 h-3" />
                  <span>Club Members</span>
                </div>
              </div>
              {isLeader && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/clubs/${slug}/events/create`)}
                  className="shrink-0"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create
                </Button>
              )}
            </div>
          </div>

          {/* Filter Tabs and View Toggle */}
          <div className="flex gap-2 items-center">
            {/* Filter tabs (only shown in list view) */}
            {viewMode === 'list' && (
              <div className="flex-1 flex gap-1 bg-oro-surface-alt border border-oro-border rounded-xl p-1">
                {[
                  { key: 'upcoming' as const, label: 'Upcoming' },
                  { key: 'all' as const, label: 'All' },
                  { key: 'past' as const, label: 'Past' }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveFilter(tab.key)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      activeFilter === tab.key
                        ? 'bg-oro-surface text-text-primary shadow-sm'
                        : 'text-text-muted hover:text-text-secondary'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* View toggle */}
            <div className="flex gap-1 bg-oro-surface-alt border border-oro-border rounded-xl p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list'
                    ? 'bg-oro-surface text-text-primary shadow-sm'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
                aria-label="List view"
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-oro-surface text-text-primary shadow-sm'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
                aria-label="Calendar view"
                title="Calendar view"
              >
                <CalendarDays className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Events Count Header (list view only) */}
          {viewMode === 'list' && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-text-muted" />
              <span className="text-text-secondary text-sm">
                {events.length} {activeFilter === 'upcoming' ? 'upcoming' : activeFilter === 'past' ? 'past' : ''}
                {events.length === 1 ? ' event' : ' events'}
              </span>
            </div>
          )}

          {/* Calendar View (#1059) */}
          {viewMode === 'calendar' && (
            <CalendarView
              events={events.map(e => ({
                id: e.id,
                title: e.title,
                starts_at: e.starts_at,
              }))}
              onDayClick={handleDayClick}
            />
          )}

          {/* Events List */}
          {viewMode === 'list' && (
            events.length > 0 ? (
              <div className="space-y-3 pb-20">
                {events.map((event) => {
                  // Format event data for EventCard component
                  const eventDate = new Date(event.starts_at * 1000);
                  const formattedDate = eventDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  });

                  return (
                    <EventCard
                      key={event.id}
                      event={{
                        id: event.id,
                        short_code: event.short_code,
                        title: event.title,
                        date: formattedDate,
                        location: event.location_name || 'Location TBD',
                        attendees: event.rsvp_count || 0,
                        imageUrl: event.card_image_url || event.hero_image_url || '', // #1962 prefers card-sized variant; COALESCE upstream handles event → club fallback
                        event_number: event.event_number ?? null
                      }}
                      onClick={() => navigate(`/event/${event.short_code || event.id}`)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="py-12">
                <EmptyState
                  icon={<Calendar className="w-12 h-12" />}
                  title={
                    activeFilter === 'upcoming'
                      ? 'No upcoming events'
                      : activeFilter === 'past'
                      ? 'No past events'
                      : 'No events yet'
                  }
                  message={
                    activeFilter === 'upcoming'
                      ? 'Check back later for new events'
                      : activeFilter === 'past'
                      ? 'This club hasn\'t hosted any events yet'
                      : 'This club hasn\'t created any events yet'
                  }
                  actionLabel={isLeader ? 'Create Event' : undefined}
                  onAction={isLeader ? () => navigate(`/clubs/${slug}/events/create`) : undefined}
                />
              </div>
            )
          )}
        </div>
      </div>

      {/* Floating Create Button for Mobile */}
      {isLeader && (
        <button
          onClick={() => navigate(`/clubs/${slug}/events/create`)}
          className="fixed bottom-20 right-4 w-14 h-14 bg-accent hover:bg-accent/90 text-black rounded-full shadow-float flex items-center justify-center transition-all duration-200 hover:shadow-float-lg"
          aria-label="Create event"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Calendar Day Modal (#1059) */}
      <CalendarDayModal
        isOpen={isDayModalOpen}
        onClose={() => setIsDayModalOpen(false)}
        date={selectedDate}
        events={selectedDayEvents}
      />
    </div>
  );
}